import React, { Fragment, useEffect, useContext, useState, useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings as AppSettings, AppActions } from 'common/types';
import { CurrencyPosition, WeekStartDays } from 'common/enums';
import { setDocumentTitle, languageList, encodeImage } from 'common/utils';
import { I18nContext, appContext } from 'common/contexts';
import { APP_NAME } from 'config';
import { CommonLayout } from '../index';
import styles from './settings.module.css';
import qrStyles from './qrcode.module.css';
import { toast } from 'react-toastify';
// @ts-ignore
import QRCode from 'qrcodejs2';

type SettingsProps = {
  settings: AppSettings;
  actions: AppActions;
};

type FormGroup = {
  id: string;
  label: string;
  c: React.ReactElement;
  description?: string;
  placeholder?: string;
  title?: string;
};

const Settings: React.FC<SettingsProps> = ({ settings, actions }) => {
  const [t, i18n] = useTranslation();
  const [formFilter, setFormFilter] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { supportedLocales } = useContext(I18nContext);
  const { state: context } = useContext(appContext);
  const [qrUrl, setQrUrl] = useState('');
  const qrRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    const title = [t('admin.title'), t('admin.settings.title')];
    setDocumentTitle(title);
  }, [t]);

  useEffect(() => {
    // Obtener el ID del padre (parentId) del contexto
    const parentId = context.parentId || '';
    
    // Construir la URL para el código QR incluyendo el tipo de tienda como query parameter
    // Usar un valor predeterminado ya que no podemos acceder a las variables de entorno directamente
    const baseUrl = 'https://movil.restomiapp.com';
    // Usar el tipo de tienda configurado o 'food' por defecto
    const storeType = settings.storeType || 'food';
    const fullUrl = `${baseUrl}/${parentId}?type=${storeType}`;
    setQrUrl(fullUrl);
    
    // Generar el código QR cuando el componente se monte
    if (qrRef.current) {
      qrRef.current.innerHTML = '';
      new QRCode(qrRef.current, {
        text: fullUrl,
        width: 200,
        height: 200,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H // alta corrección de errores
      });
    }
  }, [context.parentId, settings.storeType]); // Agregar settings.storeType como dependencia

  const handleOnChangeOfLanguagePreference = (isoCode: string) => {
    const lng = isoCode === 'default' ? '' : isoCode;
    i18n.changeLanguage(lng).then((e) => {
      actions.settings.update({ lang: isoCode });
    });
  };

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true);
      await actions.settings.saveSettings();
      toast.success(t('admin.settings.saveSuccess'));
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(t('admin.settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenUrl = () => {
    if (qrUrl) {
      window.open(qrUrl, '_blank');
    }
  };

  const handleLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    encodeImage(event, (base64: string) => {
      actions.settings.update({ logo: base64 });
      toast.success(t('admin.settings.logoUploadSuccess'));
    }, 200); // Reducir el tamaño del logo a 200px de ancho
  };

  const handleCopyUrl = () => {
    if (qrUrl) {
      navigator.clipboard.writeText(qrUrl).then(() => {
        toast.success(t('admin.settings.urlCopied'));
      });
    }
  };

  const handlePrintQr = () => {
    if (qrRef.current) {
      // Crear un nuevo documento para imprimir solo el QR
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        // Obtener el contenido del QR
        const qrContent = qrRef.current.innerHTML;
        
        // Crear el contenido HTML para la impresión
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>QR Code - ${settings.name || 'App Móvil'}</title>
            <style>
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100vh;
                margin: 0;
                padding: 20px;
                box-sizing: border-box;
                font-family: Arial, sans-serif;
              }
              .qr-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 20px;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                background-color: white;
              }
              .qr-title {
                font-size: 18px;
                font-weight: bold;
                margin-bottom: 15px;
                text-align: center;
              }
              .qr-url {
                font-size: 12px;
                color: #666;
                margin-top: 15px;
                text-align: center;
                word-break: break-all;
                max-width: 250px;
              }
              @media print {
                body {
                  height: auto;
                }
              }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <div class="qr-title">${settings.name || 'App Móvil'}</div>
              ${qrContent}
              <div class="qr-url">${qrUrl}</div>
            </div>
            <script>
              // Imprimir automáticamente y cerrar después
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  setTimeout(function() {
                    window.close();
                  }, 500);
                }, 500);
              };
            </script>
          </body>
          </html>
        `);
        
        printWindow.document.close();
      }
    }
  };

  const formGroups: FormGroup[] = [
    {
      id: 'SettingsLogo',
      label: t('admin.settings.logoLabel'),
      description: t('admin.settings.logoDescription'),
      c: (
        <div className={styles.logoContainer}>
          {settings.logo && (
            <div className={styles.logoPreview}>
              <img src={settings.logo} alt="Logo" className={styles.logoImage} />
            </div>
          )}
          <input
            type="file"
            accept="image/jpeg, image/png, image/gif"
            onChange={handleLogoUpload}
            className="form-control"
          />
        </div>
      ),
    },
    {
      id: 'SettingsStoreType',
      label: t('admin.settings.storeTypeLabel'),
      description: t('admin.settings.storeTypeDescription'),
      c: (
        <select
          className="form-select w-auto pr-5"
          value={settings.storeType || 'food'}
          onChange={(evt) => actions.settings.update({ storeType: evt.target.value })}
        >
          <option value="food">{t('admin.settings.storeTypes.food')}</option>
          <option value="clothing">{t('admin.settings.storeTypes.clothing')}</option>
        </select>
      ),
    },
    {
      id: 'SettingsLocation',
      label: t('admin.settings.locationLabel'),
      description: t('admin.settings.locationDescription'),
      c: (
        <input
          type="text"
          className="form-control"
          value={settings.location || ''}
          onChange={(evt) => actions.settings.update({ location: evt.target.value })}
        />
      ),
    },
    {
      id: 'SettingsAddress',
      label: t('admin.settings.addressLabel'),
      description: t('admin.settings.addressDescription'),
      c: (
        <input
          type="text"
          className="form-control"
          value={settings.address || ''}
          onChange={(evt) => actions.settings.update({ address: evt.target.value })}
        />
      ),
    },
    // {
    //   id: 'SettingsHasDelivery',
    //   label: t('admin.settings.hasDeliveryLabel'),
    //   c: (
    //     <div className="form-check form-switch">
    //       <input
    //         className="form-check-input"
    //         type="checkbox"
    //         id="SettingsHasDeliverySwitch"
    //         checked={settings.hasDelivery || false}
    //         onChange={(evt) => actions.settings.update({ hasDelivery: evt.target.checked })}
    //       />
    //       <label className="form-check-label" htmlFor="SettingsHasDeliverySwitch">
    //         <small>{t('admin.settings.hasDeliveryDescription')}</small>
    //       </label>
    //     </div>
    //   ),
    // },
    {
      id: 'SettingsDescription',
      label: t('admin.settings.descriptionLabel'),
      description: t('admin.settings.descriptionDescription'),
      c: (
        <textarea
          className="form-control"
          value={settings.description || ''}
          onChange={(evt) => actions.settings.update({ description: evt.target.value })}
          rows={3}
        />
      ),
    },
    // {
    //   id: 'SettingsOrganizationId',
    //   label: t('admin.settings.organizationIdLabel'),
    //   description: t('admin.settings.organizationIdDescription'),
    //   c: (
    //     <input
    //       type="text"
    //       className="form-control"
    //       value={settings.organizationId || ''}
    //       onChange={(evt) => actions.settings.update({ organizationId: evt.target.value })}
    //     />
    //   ),
    // },
    // {
    //   id: 'SettingsSection',
    //   label: t('admin.settings.sectionLabel'),
    //   description: t('admin.settings.sectionDescription'),
    //   c: (
    //     <input
    //       type="text"
    //       className="form-control"
    //       value={settings.section || ''}
    //       onChange={(evt) => actions.settings.update({ section: evt.target.value })}
    //     />
    //   ),
    // },
    // {
    //   id: 'SettingsSubsection',
    //   label: t('admin.settings.subsectionLabel'),
    //   description: t('admin.settings.subsectionDescription'),
    //   c: (
    //     <input
    //       type="text"
    //       className="form-control"
    //       value={settings.subsection || ''}
    //       onChange={(evt) => actions.settings.update({ subsection: evt.target.value })}
    //     />
    //   ),
    // },
    {
      id: 'SettingsMobileAppQr',
      label: t('admin.settings.mobileAppQrLabel'),
      description: t('admin.settings.mobileAppQrDescription'),
      c: (
        <div className={qrStyles.qrContainer}>
          <div ref={qrRef} className={qrStyles.qrCode}></div>
          <div className={qrStyles.qrUrl}>{qrUrl}</div>
          <div className={qrStyles.actionButtons}>
            <button 
              className={`${qrStyles.actionButton} ${qrStyles.primaryButton}`}
              onClick={handleOpenUrl}
              title={t('admin.settings.openUrl')}
            >
              {t('admin.settings.openUrl')}
            </button>
            <button 
              className={`${qrStyles.actionButton} ${qrStyles.secondaryButton}`}
              onClick={handleCopyUrl}
              title={t('admin.settings.copyUrl')}
            >
              {t('admin.settings.copyUrl')}
            </button>
            <button 
              className={`${qrStyles.actionButton} ${qrStyles.secondaryButton}`}
              onClick={handlePrintQr}
              title={t('admin.settings.printQr')}
            >
              {t('admin.settings.printQr')}
            </button>
          </div>
        </div>
      ),
    },
    {
      id: 'SettingsName',
      label: t('admin.settings.businessNameLabel'),
      description: t('admin.settings.businessNameDescription'),
      c: (
        <input
          type="text"
          className="form-control"
          value={settings.name}
          onChange={(evt) => actions.settings.update({ name: evt.target.value })}
        />
      ),
    },
    {
      id: 'SettingsWhatsappPhone',
      label: t('admin.settings.whatsappPhoneLabel'),
      description: t('admin.settings.whatsappPhoneDescription'),
      c: (
        <input
          type="text"
          className="form-control"
          value={settings.whatsappPhone || ''}
          onChange={(evt) => actions.settings.update({ whatsappPhone: evt.target.value })}
        />
      ),
    },
    {
      id: 'SettingsPrintReceipt',
      label: t('admin.settings.printReceiptLabel'),
      c: (
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="SettingsPrintReceiptSwitch"
            checked={settings.printReceiptByDefault}
            onChange={(evt) => actions.settings.update({ printReceiptByDefault: evt.target.checked })}
          />
          <label className="form-check-label" htmlFor="SettingsPrintReceiptSwitch">
            <small>{t('admin.settings.printReceiptDescription')}</small>
          </label>
        </div>
      ),
    },
    {
      id: 'SettingsPrintGuestCheck',
      label: t('admin.settings.disablePrintingGuestChecksLabel'),
      c: (
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="SettingsPrintGuestCheckSwitch"
            checked={settings.isDeniedPrintingGuestChecks}
            onChange={(evt) => actions.settings.update({ isDeniedPrintingGuestChecks: evt.target.checked })}
          />
          <label className="form-check-label" htmlFor="SettingsPrintGuestCheckSwitch">
            <small>{t('admin.settings.disablePrintingGuestChecksDescription')}</small>
          </label>
        </div>
      ),
    },
    {
      id: 'SettingsLanguagePreference',
      label: t('admin.settings.languagePreferenceLabel'),
      description: t('admin.settings.languagePreferenceDescription', { name: APP_NAME }),
      c: (
        <select
          className="form-select w-auto pr-5"
          value={settings.lang}
          onChange={(evt) => handleOnChangeOfLanguagePreference(evt.target.value)}
        >
          <option value="default">{t('common.browserDefault')}</option>
          {supportedLocales.map((lng) => {
            // Verificar que la clave existe en languageList antes de acceder
            const language = languageList[lng as keyof typeof languageList];
            return (
              <option key={lng} value={lng}>
                {language ? language.nativeName : lng}
              </option>
            );
          })}
        </select>
      ),
    },
    {
      id: 'SettingsFirstDayOfTheWeek',
      label: t('admin.settings.firstDayOfTheWeekLabel'),
      c: (
        <select
          className="form-select w-auto pr-5"
          value={settings.weekStartsOn}
          onChange={(evt) => actions.settings.update({ weekStartsOn: parseInt(evt.target.value, 10) })}
        >
          <option value={WeekStartDays.Auto}>{t('common.browserDefault')}</option>
          <option value={WeekStartDays.Sunday}>{t('common.weekdays.sunday')}</option>
          <option value={WeekStartDays.Monday}>{t('common.weekdays.monday')}</option>
          <option value={WeekStartDays.Saturday}>{t('common.weekdays.saturday')}</option>
        </select>
      ),
    },
    {
      id: 'SettingsCurrency',
      label: t('admin.settings.currencyLabel'),
      c: (
        <input
          type="text"
          className="form-control w-auto pr-5"
          value={settings.currency}
          onChange={(evt) => actions.settings.update({ currency: evt.target.value })}
        />
      ),
    },
    {
      id: 'SettingsCurrencyPosition',
      label: t('admin.settings.currencyPositionLabel'),
      c: (
        <select
          className="form-select w-auto pr-5"
          value={settings.currencyPosition}
          onChange={(evt) => actions.settings.update({ currencyPosition: evt.target.value as CurrencyPosition })}
        >
          <option value={CurrencyPosition.Left}>{`${settings.currency} 123.00`}</option>
          <option value={CurrencyPosition.Right}>{`123.00 ${settings.currency}`}</option>
        </select>
      ),
    },
  ];

  const renderFormGroup = ({ c, id, label, description = '', title = '', placeholder = '' }: FormGroup) => (
    <div className="mb-4" key={id} title={title}>
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      {description && <div className="form-text text-muted mb-2">{description}</div>}
      {{ ...c, props: { ...c.props, id, placeholder } }}
    </div>
  );

  // Filter form fields if required.
  const formData = formFilter
    ? formGroups.filter(
        (entity) =>
          entity.label.toLocaleLowerCase().includes(formFilter.toLocaleLowerCase()) ||
          entity.description?.toLocaleLowerCase().includes(formFilter.toLocaleLowerCase()),
      )
    : formGroups;

  const renderHead = (
    <Fragment>
      <div className="d-flex align-items-center justify-content-between w-100">
        <div className={styles.title}>{t('admin.settings.title')}</div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveSettings}
          disabled={isSaving}
        >
          {isSaving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </Fragment>
  );

  const renderBody = (
    <form>
      {formData.map((el) => renderFormGroup(el))}
    </form>
  );

  return (
    <CommonLayout
      isFilterable
      head={renderHead}
      body={renderBody}
      filterValue={formFilter}
      onChangeFilterValue={setFormFilter}
    />
  );
};

export default Settings;
